/* ==========================================================================
   AMADA 收藏 UI（手机页 + PC 抽屉的渲染 / 步进器 / 删除 / 批量加购）
   --------------------------------------------------------------------------
   数据层是 window.AmadaFavorites（assets/amada-favorites.js，先于本文件加载）。
   本文件只管「把数据画成卡片 + 处理页面内交互」，不碰数据结构。

   适用两个载体，共用同一套渲染：
     · 手机页  section.amada-fav-page（/pages/favorites）
     · PC 抽屉  snippet.amada-fav-drawer

   稿：2026-08-26 终稿 tPnjQ1EbVgBhInGroWrTN6（PC 5:243 / Mobile 12:4203）；
       历史规格见 figma-qa/specs/favorites.md
   本轮新增：卡片右上角**勾选框**——勾选决定「计入合计」与「加进购物车」，
       默认全选，逐条说明见下方 FavoritesView 里 `selected` 的注释。
   ========================================================================== */
(function () {
  'use strict';

  if (!window.AmadaFavorites) return; // 数据层没加载就不启动，避免报错

  var F = window.AmadaFavorites;

  /* money 格式化：优先用主题的格式串，回退到 $x.xx。
     Shopify 价格是「分」的整数。 */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (window.theme && typeof window.theme.moneyFormat === 'string' && window.Shopify && window.Shopify.formatMoney) {
      try {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      } catch (e) {
        /* 落回下面的简单格式 */
      }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* 卡片模板（liquid 里的 <template>）。
     手机页 section 和 PC 抽屉 snippet 各自渲染了一份 template（两者可同页并存：
     抽屉在 theme.liquid 全站渲染，手机页是 /pages/favorites 的 section）。
     所以必须从**各自的 root 内**取 template，不能用全局 document.querySelector——
     否则两个 View 都抓到第一个 template、计数/合计选择器也会串到另一个载体上。 */
  function cardTemplate(root) {
    var tpl = (root || document).querySelector('[data-favorites-card-template]');
    return tpl && tpl.content ? tpl : null;
  }

  /* ------------------------------------------------------------------
     一个「载体」= 一块要渲染收藏列表的区域（页 or 抽屉）。
     传入它的根元素，**所有插槽都在 root 内查找**，与另一个载体完全隔离。
     ------------------------------------------------------------------ */
  function FavoritesView(root) {
    var grid = root.querySelector('[data-favorites-grid]');
    var empty = root.querySelector('[data-favorites-empty]');
    var footer = root.querySelector('[data-favorites-footer]');
    var totalEl = root.querySelector('[data-fav-total]');
    var countEls = root.querySelectorAll('[data-fav-count-num]');
    var addAllBtn = root.querySelector('[data-fav-add-all]');

    if (!grid) return null;

    var resolvedCache = []; // 最近一次 resolve() 结果，加购/合计都基于它
    var renderSeq = 0; // 渲染序列号，防 resolve 异步竞态
    var lastRows = []; // 最近一次画出去的行（勾选切换时不重画，直接拿它重算合计）

    /* ------------------------------------------------------------------
       勾选状态（2026-08-26 终稿新增，稿 5:295 / 12:4225 卡片右上角勾选框）
       --------------------------------------------------------------------
       语义：**勾选决定「计不计入合计」「加不加进购物车」**。
       稿上底部就是「合计 + ADD TO CART」，而稿里画的是「前两个选中、其余未选」——
       那只是示意，真正的默认态稿里看不出来。
       ⚠️ 按 CLAUDE.md「交互态不能从静态稿反推」：这里选**默认全选**
          （否则一打开抽屉合计就是 $0.00、按钮点了没反应，底部那块信息等于空转），
          并已在交付报告里列为待运营确认项。

       存哪：只存在本 view 的内存里，**不写 localStorage**——
       勾选是「这一次要加购哪些」的临时意图，不是收藏数据本身；
       写进 amada-favorites.js 的存储会污染数据层（那个文件全站心形都依赖，禁止改）。
       页/抽屉各有一份，互不影响；刷新后回到默认全选。

       用 `!== false` 判定：没记录过的 key（新收藏的商品）自动算选中，不用在 render 里补种子。
       ------------------------------------------------------------------ */
    var selected = {};

    function isSelected(key) {
      return selected[key] !== false;
    }

    /* 用 resolve() 拉回真实价格/库存后重画。
       快照先渲染（避免白屏），resolve 完成再刷新价格与置灰。
       ⚠️ resolve 是异步网络请求，多次 render（快速连点心形 / 初始化多个 change 事件）
       发出的请求**完成顺序不保证**——携带旧 list 的请求若最后返回，会用过期数据覆盖。
       用序列号丢弃过期响应：只有最新一次 render 的 resolve 才允许 paint。 */
    function render() {
      var items = F.list();
      var seq = ++renderSeq;

      /* 即时渲染用的行数据：**已经 resolve 过的商品直接复用上次结果**，只把 qty 换成最新的。
         ⚠️ 不能无脑退回 snap 快照：快照里的图片 URL 与 /products/xx.js 返回的 URL 不一定一样，
            每次改数量都会「快照 URL → resolve URL」换一次 src，图片就会闪
            （2026-07-28 运营反馈的第三个来源，前两个在 paint()/fillCard 的注释里）。
            只有从没 resolve 过的新条目才用快照兜底，避免白屏。 */
      var cacheByKey = {};
      resolvedCache.forEach(function (r) { cacheByKey[rowKey(r)] = r; });

      paint(items.map(function (it) {
        var cached = cacheByKey[rowKey(it)];
        if (cached) {
          var merged = {};
          Object.keys(cached).forEach(function (k) { merged[k] = cached[k]; });
          merged.qty = it.qty; // 数量以最新的为准，其余（图/价/库存）沿用已 resolve 的
          return merged;
        }
        return {
          handle: it.handle, variantId: it.variantId, qty: it.qty,
          title: it.snap.title, image: it.snap.image,
          price: it.snap.price, comparePrice: it.snap.comparePrice,
          available: true, unavailable: false, url: '/products/' + it.handle
        };
      }));

      /* 只改数量时不必重新拉一遍价格/库存：条目集合没变（key 一一对应）就跳过 resolve。
         少一次网络往返 = 少一次「网络回来后再 paint 一遍」，也省掉 +/− 连点时
         每点一下打一串 /products/xx.js 的浪费。
         条目增删（收藏/取消/删除）时 key 集合会变，照常 resolve。 */
      var sameSet =
        resolvedCache.length === items.length &&
        items.every(function (it) { return !!cacheByKey[rowKey(it)]; });
      if (sameSet) {
        // 把最新数量同步回 cache，合计/加购口径才不会用旧数量
        resolvedCache.forEach(function (r) {
          for (var i = 0; i < items.length; i++) {
            if (rowKey(items[i]) === rowKey(r)) { r.qty = items[i].qty; break; }
          }
        });
        updateTotal(resolvedCache);
        return;
      }

      // 再拉真实数据刷新（过期响应直接丢弃）
      F.resolve(items).then(function (resolved) {
        if (seq !== renderSeq) return; // 已有更新的 render，本次结果作废
        resolvedCache = resolved;
        paint(resolved);
      });
    }

    /* 卡片身份：同一商品的同一变体 = 同一张卡，用它在重绘之间复用 DOM */
    function rowKey(row) {
      return row.handle + '|' + (row.variantId == null ? '' : row.variantId);
    }

    /* ⚠️ 这里**不能**用 `grid.innerHTML=''` 全量重建（2026-07-28 运营反馈「改数量时图片闪，
       动第 1 个商品的数量第 3 个商品的图也闪」的根因）：
         · 重建会把每个 <img> 的 src 重新赋值一次 → 浏览器重新解码/取图 → 整排卡片一起闪；
         · 而点一次 +/− 会触发**两次** paint —— 先用本地快照画一次，等 resolve() 网络回来再画一次，
           两次的图片 URL 还可能不完全一致（快照存的 URL vs /products/xx.js 返回的 URL），
           来回换 src 闪得更明显；
         · 顺带把按钮 DOM 也销毁了，连点 + 时焦点丢失、手感发飘。
       改成按 key 复用：存在就原地更新、不存在才 clone、多余的删掉，顺序用 appendChild 重排
       （appendChild 已在文档里的节点是「移动」不是「重建」，不会重新加载图片）。 */
    function paint(rows) {
      var tpl = cardTemplate(root); // 从本载体自己的 root 内取模板，与另一载体隔离
      var count = rows.length;

      lastRows = rows;

      // 计数（页头 N ITEMS）
      Array.prototype.forEach.call(countEls, function (el) { el.textContent = String(count); });

      // 空状态
      if (count === 0) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        if (footer) footer.hidden = true;
        return;
      }
      if (empty) empty.hidden = true;
      if (footer) footer.hidden = false;
      if (!tpl) return;

      // 现有卡片按 key 建索引（同 key 多张时只留第一张，其余当多余删掉）
      var existing = {};
      Array.prototype.forEach.call(grid.querySelectorAll('.amada-fav-card'), function (el) {
        var k = el.getAttribute('data-key');
        if (k && !existing[k]) existing[k] = el;
        else el.parentNode.removeChild(el);
      });

      var used = {};
      rows.forEach(function (row) {
        var key = rowKey(row);
        var node = existing[key];
        if (node) {
          used[key] = true;
        } else {
          node = tpl.content.firstElementChild.cloneNode(true);
          node.setAttribute('data-key', key);
        }
        fillCard(node, row);
        grid.appendChild(node); // 已在文档里的节点：移动到末尾，不重建
      });

      // 删掉这轮没用到的（被移除收藏的商品）
      Object.keys(existing).forEach(function (k) {
        if (!used[k] && existing[k].parentNode) existing[k].parentNode.removeChild(existing[k]);
      });

      updateTotal(rows);
    }

    function fillCard(node, row) {
      node.setAttribute('data-handle', row.handle);
      node.setAttribute('data-variant-id', row.variantId == null ? '' : row.variantId);
      node.classList.toggle('is-unavailable', !!row.unavailable || row.available === false);

      // 勾选态（卡片可能是复用的旧 DOM，每次都要重写一遍）
      paintSelection(node, isSelected(rowKey(row)));

      var img = node.querySelector('[data-fav-img]');
      if (img) {
        if (row.image) {
          /* ⚠️ 只在真的变了才写 src —— 哪怕赋同一个值，浏览器也会重新走一遍取图/解码，
             这就是「改数量整排图片闪」的第二个来源。
             必须比 getAttribute('src')（原始字符串）而不是 img.src（已解析成绝对 URL，
             与快照里存的相对/协议省略 URL 永远不相等 → 判断恒为真，等于没做优化）。 */
          if (img.getAttribute('src') !== row.image) img.src = row.image;
          img.alt = row.title || '';
        } else {
          img.removeAttribute('src');
          img.alt = '';
        }
      }

      var href = row.url || '/products/' + row.handle;

      var title = node.querySelector('[data-fav-title]');
      if (title) { title.textContent = row.title || ''; title.href = href; }

      // 图片链接与标题指向同一个地址（运营 07-28：点图也要能进商品页）
      var mediaLink = node.querySelector('[data-fav-link]');
      if (mediaLink) mediaLink.href = href;

      var price = node.querySelector('[data-fav-price]');
      if (price) price.textContent = row.price != null ? formatMoney(row.price) : '';

      var compare = node.querySelector('[data-fav-compare]');
      if (compare) {
        // 只有原价高于现价才显示划线价
        if (row.comparePrice != null && row.price != null && row.comparePrice > row.price) {
          compare.textContent = formatMoney(row.comparePrice);
          compare.hidden = false;
        } else {
          compare.textContent = '';
          compare.hidden = true;
        }
      }

      var qty = node.querySelector('[data-fav-qty]');
      if (qty) qty.textContent = String(row.qty || 1);

      // 数量减到 1 时禁用「−」（不做删除，删除走 ✕）
      var dec = node.querySelector('[data-fav-dec]');
      if (dec) dec.disabled = (row.qty || 1) <= 1;
    }

    /* 把一张卡的勾选态写到 DOM 上（class + aria-pressed，两处都要，
       CSS 认 .is-selected，读屏认 aria-pressed） */
    function paintSelection(node, on) {
      node.classList.toggle('is-selected', !!on);
      var btn = node.querySelector('[data-fav-select]');
      if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    /* 计入合计/加购的行：可售 + 已勾选。两处口径必须一致，
       否则会出现「合计有钱、点加购却什么都没加」。 */
    function payableRows(rows) {
      return (rows || []).filter(function (r) {
        return r.available !== false && !r.unavailable && isSelected(rowKey(r));
      });
    }

    function updateTotal(rows) {
      var picked = payableRows(rows);

      if (totalEl) {
        var sum = 0;
        picked.forEach(function (r) {
          if (typeof r.price === 'number') sum += r.price * (r.qty || 1);
        });
        totalEl.textContent = formatMoney(sum);
      }

      // 一件未勾选时禁用加购，别留个点了没反应的死按钮
      if (addAllBtn && !addAllBtn.classList.contains('is-loading')) {
        addAllBtn.disabled = picked.length === 0;
      }
    }

    /* ---- 交互：事件委托挂在 grid 上，兼容 JS 后填充的卡片 ---- */
    grid.addEventListener('click', function (e) {
      var card = e.target.closest('.amada-fav-card');
      if (!card) return;
      var handle = card.getAttribute('data-handle');
      var vid = card.getAttribute('data-variant-id') || null;

      if (e.target.closest('[data-fav-remove]')) {
        F.remove(handle, vid); // 数据层广播 change → 本 view 的订阅回调会 render()
        return;
      }
      if (e.target.closest('[data-fav-select]')) {
        /* 勾选切换：**不走 render()**。
           render 会重新 resolve/repaint，一是没必要（数据没变），
           二是会把刚点的按钮 DOM 换掉、焦点丢失。这里只改本卡 + 重算合计。 */
        var key = handle + '|' + (vid == null ? '' : vid);
        selected[key] = !isSelected(key);
        paintSelection(card, selected[key]);
        updateTotal(resolvedCache.length ? resolvedCache : lastRows);
        return;
      }
      if (e.target.closest('[data-fav-inc]')) {
        var cur = getQty(handle, vid);
        F.setQty(handle, vid, cur + 1);
        return;
      }
      if (e.target.closest('[data-fav-dec]')) {
        var cur2 = getQty(handle, vid);
        if (cur2 > 1) F.setQty(handle, vid, cur2 - 1);
        return;
      }
    });

    function getQty(handle, vid) {
      var items = F.list();
      for (var i = 0; i < items.length; i++) {
        if (items[i].handle === handle && items[i].variantId === (vid || null)) return items[i].qty;
      }
      return 1;
    }

    /* ---- 批量加购 ---- */
    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        addAllToCart(addAllBtn);
      });
    }

    function addAllToCart(btn) {
      /* 用最近一次 resolve 的结果，过滤掉不可售的（该接口一条失败整单 422）
         **以及未勾选的**（2026-08-26 终稿：勾选框决定加购范围，旧实现是无条件全部加购）。
         口径与 updateTotal 共用 payableRows()，保证「合计里有的」和「加进购物车的」一致。 */
      var items = payableRows(resolvedCache)
        .filter(function (r) { return r.variant; })
        .map(function (r) { return { id: r.variant.id, quantity: r.qty || 1 }; });

      if (!items.length) return;

      var busy = 'is-loading';
      btn.classList.add(busy);
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (j) { throw j; });
          return r.json();
        })
        .then(function () {
          // 成功：保留收藏（收藏≠购物车），打开购物车抽屉。
          // 开抽屉统一交给 window.AmadaCartDrawer（见下方 openCartDrawer 注释）。
          openCartDrawer();
        })
        .catch(function () {
          // 加购失败：给个可感知的反馈，不静默
          btn.classList.add('is-error');
          window.setTimeout(function () { btn.classList.remove('is-error'); }, 2000);
        })
        .finally(function () {
          btn.classList.remove(busy);
          /* ⚠️ 不要无脑 `btn.disabled = false`：勾选数为 0 时按钮本就该是禁用的，
             写死 false 会把「一件没勾」的禁用态解掉。交回 updateTotal 统一判定。 */
          btn.disabled = false;
          updateTotal(resolvedCache.length ? resolvedCache : lastRows);
        });
    }

    // 订阅数据变化：本页操作、其它标签页、程序调用都会触发重画
    F.subscribe(function () { render(); });

    render();
    return { render: render };
  }

  /* 🚨 2026-08-27 返修（figma-qa/reports/audit-cart-favorites.md A-2）：
     这里原来自己抄了一份「点 header 购物袋 → 400ms 后看抽屉开没开」，
     探测开态用的是 `.quick-cart.is-active / [aria-hidden="false"] / .quick-cart--visible`
     —— 主题真实的开态标记只有 `.quick-cart__wrapper.active`
     （assets/theme.js `selectors$v.cartWrapper` + `classes$8.active`），三个选择器
     一个都命不中，于是**批量加购成功后必然被强制整页跳 /cart**（实测 3.25s 后导航）。

     现在统一走 sections/quick-cart.liquid 导出的 window.AmadaCartDrawer，
     一份实现两处用（另一处是 assets/amada-quickview.js），
     等待改成 MutationObserver「等到真的开」而不是猜 400ms。 */
  function openCartDrawer() {
    if (window.AmadaCartDrawer && typeof window.AmadaCartDrawer.open === 'function') {
      window.AmadaCartDrawer.open({ justAdded: true });
      return;
    }
    // quick-cart section 被运营关掉时才走整页购物袋 —— 那才是该跳的场景
    window.location.href = '/cart';
  }

  /* ------------------------------------------------------------------
     PC 抽屉控制器：开合 + 断点分流 + overlay/ESC 关闭 + scroll lock
     ------------------------------------------------------------------ */
  var DESKTOP_QUERY = '(min-width: 960px)';

  function initDrawer() {
    var rootWrap = document.querySelector('[data-favorites-drawer-root]');
    if (!rootWrap) return;

    var view = FavoritesView(document.querySelector('[data-favorites-drawer]'));
    var overlay = rootWrap.querySelector('[data-fav-drawer-overlay]');
    var closeBtn = rootWrap.querySelector('[data-fav-drawer-close]');
    var lastFocus = null;

    function isOpen() {
      return rootWrap.classList.contains('is-open');
    }

    function open() {
      lastFocus = document.activeElement;
      rootWrap.classList.add('is-open');
      rootWrap.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('amada-fav-drawer-open'); // scroll lock 钩子
      if (view) view.render(); // 打开时刷新到最新
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      rootWrap.classList.remove('is-open');
      rootWrap.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('amada-fav-drawer-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    if (overlay) overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });

    /* header 心形：桌面拦截跳转、开抽屉；移动端放行（跳 /pages/favorites，JS 失效兜底）。
       用事件委托挂 document，兼容 sticky header 重建。 */
    document.addEventListener(
      'click',
      function (e) {
        var trigger = e.target.closest('[data-favorites-open]');
        if (!trigger) return;
        if (!window.matchMedia(DESKTOP_QUERY).matches) return; // 移动端不接管
        e.preventDefault();
        open();
      },
      false
    );

    /* ------------------------------------------------------------------
       商品心形 → 自动弹出 Favorites 侧边栏（2026-09-07 运营验收 #39）
       ------------------------------------------------------------------
       口径沿革（这条口径已经翻过三次，改之前先看完再动）：
         2026-07-25 拍板「桌面加入收藏后自动弹抽屉」
         2026-07-28 新稿 32:882 改成「心形旁弹带 VIEW 的提示条」→ 自动弹抽屉下线
         2026-09-03 运营明确**不要**那条提示条（snippets/amada-favorites-toast.liquid
                    里的 amada_fav_toast_enabled 已置 false，DOM 不渲染 → initToast 空转）
         2026-09-07 运营 #39：**所有爱心点击后都要弹出 Favorites 侧边栏** ← 现行口径
       于是「收藏成功」的反馈重新回到抽屉，与提示条不会重复（提示条已关）。

       规则：
         · 只有「加入收藏」才弹，**取消收藏不弹**（与 initToast 同一判定口径：
           不重复做 toggle，读数据层已经写好的 is-favorited / aria-pressed）。
         · 覆盖全站所有 [data-favorites-toggle]：分类页 / 搜索页 / sale 页商品卡、
           PDP 右栏、吸底浮条、PERFECT PAIRINGS、图区与标题旁的心形、
           以及将来任何新入口 —— 走 document 委托，动态注入的 DOM 也命中。
         · PC / 移动端**都弹**：抽屉面板是 700px + max-width:92vw，375 下自适应，
           不再走「移动端跳 /pages/favorites」那条（那条只保留给 header 心形）。

       🧱 层叠秩序（详见 assets/amada-cart-drawer.css 第 8 节）：
          2200 抽屉（amada-drawer / amada-fav-drawer）＜ 2300 弹窗 ＜ 2400 购物袋抽屉。
          收藏抽屉与通用抽屉（quick view / PDP 六个抽屉）**同在 2200**，叠在一起谁在后
          谁盖住，属于未定义行为，而且会出现「两层遮罩 + 背景锁两次」。
          所以在通用抽屉里点心形时：**先 closeAll() 关掉它，等关闭动画走完再开收藏抽屉**，
          任何时刻只存在一层。等待 320ms 与 amada-drawer.js 的 TRANSITION_MS=300 对齐
          （少等会看到两层同框，多等用户会觉得卡）。
       ⚠️ 收藏抽屉**自己内部**的心形（若将来卡片模板加了）不再重复开自己，直接返回。
       ------------------------------------------------------------------ */
    var DRAWER_SWAP_MS = 320; // = amada-drawer.js 的 TRANSITION_MS(300) + 一帧余量

    document.addEventListener(
      'click',
      function (e) {
        var heart = e.target.closest && e.target.closest('[data-favorites-toggle][data-product-handle]');
        if (!heart) return;
        if (heart.closest('[data-favorites-drawer-root]')) return; // 抽屉里的心形不弹自己

        /* 放到下一个任务里读：数据层 amada-favorites.js 的委托先注册、先执行，
           这时 is-favorited / aria-pressed 已经写完（同 initToast 的手法）。 */
        window.setTimeout(function () {
          var added =
            heart.classList.contains('is-favorited') || heart.getAttribute('aria-pressed') === 'true';
          if (!added) return; // 取消收藏 → 不弹

          var host = heart.closest('[data-amada-drawer]');
          if (host && window.AmadaDrawer && typeof window.AmadaDrawer.closeAll === 'function') {
            window.AmadaDrawer.closeAll();
            window.setTimeout(open, DRAWER_SWAP_MS);
            return;
          }
          open();
        }, 0);
      },
      false
    );

    window.AmadaFavoritesDrawer = { open: open, close: close, isOpen: isOpen };
  }

  /**
   * 收藏页 BACK 按钮：回到「用户点心形进来的那个商品页」，而不是首页。
   *
   * 运营 2026-07-27 反馈：点 BACK 跳回了主页。根因是 liquid 里 href 写死
   * routes.root_url、且没有任何 JS 接管。
   * 做法：同站来路才跳（外站来的、直接输网址的保持首页兜底，避免跳出站点）。
   * 用 location.href 而不是 history.back()：back 会命中 bfcache，
   * 商品页上的心形状态（读 localStorage 渲染）不会重新计算，回去看到的是旧态。
   */
  function initBackLink() {
    document.addEventListener(
      'click',
      function (e) {
        var back = e.target.closest && e.target.closest('[data-fav-back]');
        if (!back) return;
        var ref = document.referrer;
        if (!ref) return; // 无来路 → 走 href 的首页兜底
        var url;
        try {
          url = new URL(ref);
        } catch (err) {
          return;
        }
        if (url.origin !== window.location.origin) return; // 外站来的不跳回去
        if (url.pathname === window.location.pathname) return; // 来路就是收藏页自己，别原地打转
        e.preventDefault();
        e.stopPropagation();
        window.location.href = ref;
      },
      true // 捕获阶段：抢在主题的冒泡委托之前拿到这次点击
    );
  }

  /**
   * 收藏成功提示（PC 心形旁弹窗 / 移动端底部浮窗）
   * 稿：2026-07-28 副本 oUYGbcZgTMAzAQfYnIstDR，PC 32:882 / 移动 22:2437。
   *
   * 只在「加入收藏」时弹，取消收藏不弹（与旧的自动开抽屉同一判定口径）。
   * ⚠️ 不重复做 toggle：数据层 amada-favorites.js 的委托先注册，
   *    同一次点击它已把 is-favorited / aria-pressed 写好，这里只读结果。
   */
  function initToast() {
    var toast = document.querySelector('[data-favorites-toast]');
    if (!toast) return;

    var AUTO_HIDE_MS = 4000;
    /* 稿 PC：面板顶 = 心形底 + 22；面板右缘 = 心形右缘 + 64 */
    var PC_GAP_Y = 22;
    var PC_OVERHANG_X = 64;
    var VIEWPORT_MARGIN = 16; // 贴近视口边缘时的夹取余量（稿只画了 1920，这是自适应兜底）

    var timer = null;
    var anchor = null;

    function isDesktop() {
      return window.matchMedia(DESKTOP_QUERY).matches;
    }

    /* 移动端：PDP 有 80 高的浮动加购条，提示条要抬到它上面，别压住 ADD 按钮 */
    function mobileLift() {
      var bar = document.querySelector('.sticky-atc-bar');
      if (!bar) return 0;
      var style = window.getComputedStyle(bar);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return 0;
      var r = bar.getBoundingClientRect();
      if (!r.height) return 0;
      // 只有真的贴在视口底部时才抬（加购条未吸附时不占位）
      if (r.bottom < window.innerHeight - 4 || r.top > window.innerHeight) return 0;
      return Math.round(r.height);
    }

    function position() {
      if (!isDesktop()) {
        toast.style.top = '';
        toast.style.left = '';
        toast.style.setProperty('--amada-fav-toast-lift', mobileLift() + 'px');
        return;
      }
      toast.style.removeProperty('--amada-fav-toast-lift');
      if (!anchor || !anchor.isConnected) return;

      var a = anchor.getBoundingClientRect();
      var w = toast.offsetWidth;
      var h = toast.offsetHeight;

      var left = a.right + PC_OVERHANG_X - w;
      var top = a.bottom + PC_GAP_Y;

      var maxLeft = window.innerWidth - VIEWPORT_MARGIN - w;
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, Math.max(VIEWPORT_MARGIN, maxLeft)));
      var maxTop = window.innerHeight - VIEWPORT_MARGIN - h;
      top = Math.max(VIEWPORT_MARGIN, Math.min(top, Math.max(VIEWPORT_MARGIN, maxTop)));

      toast.style.left = Math.round(left) + 'px';
      toast.style.top = Math.round(top) + 'px';
    }

    function hide() {
      window.clearTimeout(timer);
      timer = null;
      toast.classList.remove('is-open');
      anchor = null;
      // 等淡出动画结束再 hidden，避免下次打开时闪一下旧位置
      window.setTimeout(function () {
        if (!toast.classList.contains('is-open')) toast.hidden = true;
      }, 220);
    }

    function show(el) {
      anchor = el || null;
      toast.hidden = false;
      // 先量一次（hidden 时量不到尺寸），再定位，最后才加 is-open
      position();
      // 强制回流，保证 transition 从初始态开始
      void toast.offsetWidth;
      toast.classList.add('is-open');
      window.clearTimeout(timer);
      timer = window.setTimeout(hide, AUTO_HIDE_MS);
    }

    document.addEventListener(
      'click',
      function (e) {
        var el = e.target.closest && e.target.closest('[data-favorites-toggle][data-product-handle]');
        if (!el) return;
        // 放到下一个任务里读，确保数据层的 class/aria 已经写完
        window.setTimeout(function () {
          var added =
            el.classList.contains('is-favorited') || el.getAttribute('aria-pressed') === 'true';
          if (added) show(el);
          else hide();
        }, 0);
      },
      false
    );

    toast.addEventListener('click', function (e) {
      if (e.target.closest('[data-fav-toast-close]')) {
        hide();
        return;
      }
      if (e.target.closest('[data-fav-toast-view]')) {
        hide();
        // PC 有抽屉 → 直接滑出；移动端没有抽屉 → 去收藏页
        if (isDesktop() && window.AmadaFavoritesDrawer) window.AmadaFavoritesDrawer.open();
        else window.location.href = '/pages/favorites';
      }
    });

    // 打开期间跟随心形（PC 用 fixed 定位，页面一滚就会脱锚）
    window.addEventListener(
      'scroll',
      function () {
        if (toast.classList.contains('is-open')) position();
      },
      true
    );
    window.addEventListener('resize', function () {
      if (toast.classList.contains('is-open')) position();
    });
  }

  function init() {
    // 手机页
    document.querySelectorAll('[data-favorites-page]').forEach(function (root) {
      FavoritesView(root);
    });
    // PC 抽屉
    initDrawer();
    // BACK 返回来路页
    initBackLink();
    // 收藏成功提示（PC 弹窗 / 移动浮窗）
    initToast();
  }

  window.AmadaFavoritesUI = { FavoritesView: FavoritesView, openCartDrawer: openCartDrawer };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
